import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import api from "../services/api";
import { useTranslation } from "react-i18next";
import { ThemeContext } from "../providers/ThemeProvider";

const Market = () => {
    const { t, i18n } = useTranslation();
    const { isAuthenticated } = useAuth();
    const { theme } = useContext(ThemeContext);
    const navigate = useNavigate();
    const [url, setUrl] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const getMarket = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Get market URL from API
            const response = await api.get('/trader/client/Trader/GetTraderUrl');
            
            if (response && response.data) {
                // Get current language from i18n or default to 'tr'
                const currentLang = i18n.language || "tr";
                
                // Append language and theme parameters to the URL
                const marketUrl = `${response.data}&lang=${currentLang}&theme=${theme}`;
                setUrl(marketUrl);
            } else {
                throw new Error('Market URL not found');
            }
        } catch (error) {
            console.error('Error fetching market URL:', error);
            setError(t('market.marketDataError'));
            
            // If 401 error, redirect to login
            if (error.response?.status === 401) {
                localStorage.removeItem('auth:token');
                localStorage.removeItem('auth:user');
                navigate('/login');
                window.location.reload();
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Check if user is authenticated
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }

        getMarket();
        
        // Scroll to top when component mounts
        window.scrollTo(0, 0);
    }, [isAuthenticated, navigate]);

    // Update URL when theme changes
    useEffect(() => {
        if (url && !loading) {
            getMarket();
        }
    }, [theme]);

    // Show loading state
    if (loading) {
        return (
            <div className="market-loading">
                <div className="container">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">{t('market.loading')}</span>
                            </div>
                            <p className="mt-3">{t('market.loadingMarketData')}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Show error state
    if (error) {
        return (
            <div className="market-error">
                <div className="container">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                        <div className="text-center">
                            <div className="alert alert-danger">
                                <h4>{t('market.error')}</h4>
                                <p>{error}</p>
                                <button 
                                    className="btn btn-primary" 
                                    onClick={() => {
                                        setError(null);
                                        getMarket();
                                    }}
                                >
                                    {t('market.retry')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="market-page">
            <div className="market-iframe-container">
                {url && (
                    <iframe
                        width="100%"
                        height="100%"
                        id="webtrader"
                        title="webtrader"
                        src={url}
                        frameBorder="0"
                        allowFullScreen
                        onLoad={(e) => {
                            // Reset scroll position when iframe loads
                            try {
                                e.target.contentWindow.scrollTo(0, 0);
                            } catch (err) {
                                // Cross-origin restriction - iframe içeriğine erişilemez
                                console.log('Cannot access iframe content due to CORS');
                            }
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default Market;

